/**
 * API Client Wrapper
 * Executes API calls and applies optional normalization.
 * @module api/contractClient
 */

/**
 * Execute API call with optional normalization
 * @param {Object} options - Options object
 * @param {string} options.endpoint - API endpoint
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} options.params - URL params (for GET requests)
 * @param {Object} options.data - Request body (for POST/PUT requests)
 * @param {Function} options.normalizer - Optional normalizer function
 * @param {Object} options.api - Optional API client (defaults to imported api module)
 * @returns {Promise<Object>} API response data (normalized when normalizer provided)
 */
export async function contractRequest({
  endpoint,
  method = "GET",
  params,
  data,
  normalizer,
  api: apiClient,
}) {
  // Execute API request using axios methods
  let response
  const upperMethod = method.toUpperCase()

  if (upperMethod === "GET") {
    response = await apiClient.get(
      endpoint,
      params && Object.keys(params).length > 0 ? { params } : undefined
    )
  } else if (upperMethod === "POST") {
    response = await apiClient.post(endpoint, data, undefined)
  } else if (upperMethod === "PUT") {
    response = await apiClient.put(endpoint, data, undefined)
  } else if (upperMethod === "DELETE") {
    // Support data in DELETE request for bulk operations
    if (data) {
      response = await apiClient.delete(endpoint, { data })
    } else {
      response = await apiClient.delete(
        endpoint,
        params && Object.keys(params).length > 0 ? { params } : undefined
      )
    }
  } else {
    throw new Error(`Unsupported HTTP method: ${method}`)
  }

  const responseData = response?.data

  // Handle Blob/file responses (e.g., exports) - skip schema validation
  if (responseData instanceof Blob || responseData instanceof ArrayBuffer) {
    if (normalizer) {
      return normalizer(responseData)
    }
    return responseData
  }

  // Normalize raw response data for frontend consumption
  if (normalizer) {
    return normalizer(responseData)
  }

  return responseData
}

/**
 * Create a contract client for a specific endpoint
 * @param {Object} config - Configuration
 * @param {string} config.baseEndpoint - Base endpoint URL
 * @param {import("zod").ZodSchema} config.schema - Default schema
 * @param {Function} config.defaultNormalizer - Default normalizer
 * @returns {Object} Contract client
 */
export function createContractClient(
  api,
  baseEndpoint,
  _schema,
  defaultNormalizer = (data) => data
) {
  /**
   * Compose endpoint for child-resource methods.
   *
   * @precondition baseEndpoint is a non-empty route segment without the /api prefix.
   * @precondition firstArg is undefined/null/empty (base endpoint), absolute path starting with '/', or child identifier segment.
   * @postcondition returns baseEndpoint when firstArg is empty.
   * @postcondition returns firstArg unchanged when firstArg starts with '/'.
   * @postcondition returns `${baseEndpoint}/${firstArg}` (single slash join) for relative child ids.
   * @mutates none
   * @throws none
   */
  const joinEndpoint = (firstArg) => {
    if (!firstArg) {
      return baseEndpoint
    }

    if (firstArg.startsWith("/")) {
      return firstArg
    }

    return baseEndpoint.endsWith("/")
      ? `${baseEndpoint}${firstArg}`
      : `${baseEndpoint}/${firstArg}`
  }

  // Track endpoint calls for testing
  const calls = {
    get: [],
    post: [],
    put: [],
    delete: [],
  }

  return {
    calls,
    get: async (firstArg, options = {}) => {
      const endpoint = joinEndpoint(firstArg)
      calls.get.push({ endpoint })
      return contractRequest({
        api,
        endpoint,
        method: "GET",
        params: options.params,
        normalizer: defaultNormalizer,
      })
    },
    // eslint-disable-next-line no-unused-vars
    post: async (data, _options = {}) => {
      calls.post.push({ endpoint: baseEndpoint })
      return contractRequest({
        api,
        endpoint: baseEndpoint,
        method: "POST",
        data,
        normalizer: defaultNormalizer,
      })
    },
    // eslint-disable-next-line no-unused-vars
    put: async (firstArg, data, _options = {}) => {
      const endpoint = joinEndpoint(firstArg)
      calls.put.push({ endpoint })
      return contractRequest({
        api,
        endpoint,
        method: "PUT",
        data,
        normalizer: defaultNormalizer,
      })
    },
    delete: async (firstArg, options = {}) => {
      const endpoint = joinEndpoint(firstArg)
      calls.delete.push({ endpoint })
      return contractRequest({
        api,
        endpoint,
        method: "DELETE",
        params: options.params,
        normalizer: defaultNormalizer,
      })
    },
  }
}

/**
 * Wrap an existing API call with contract validation
 * @param {Function} apiCall - Original API call function
 * @param {Object} options - Options
 * @param {import("zod").ZodSchema} options.schema - Zod schema
 * @param {Function} options.normalizer - Normalizer function
 * @returns {Function} Wrapped API call
 */
export function withContract(apiCallOrSchema, optionsOrApiCall) {
  let apiCall, normalizer

  // Detect which signature is being used
  if (typeof apiCallOrSchema === "function") {
    // New signature: withContract(apiCall, { schema, normalizer })
    // Note: schema is intentionally ignored in runtime.
    apiCall = apiCallOrSchema
    normalizer = optionsOrApiCall?.normalizer
  } else {
    // Old signature: withContract(schema, apiCall)
    // Note: schema is intentionally ignored in runtime.
    apiCall = optionsOrApiCall
    normalizer = undefined
  }

  return async (...args) => {
    const response = await apiCall(...args)
    const responseData = response?.data

    // Runtime path: return raw response or normalized response only.
    if (normalizer) {
      return normalizer(responseData)
    }

    return responseData
  }
}
